import { Moment } from 'moment';
import {
  PricingDetails,
  ContainerType,
  CurrencyCode,
  ContainerGetAllQuery,
  RepairGetAllGroupQuery,
} from '../types';
import { YardPlaceSceneData } from '../design-system';

export const pricingDetails: PricingDetails = [
  'loaded',
  'empty',
  'loadedCraneIn',
  'loadedCraneOut',
  'emptyCraneIn',
  'emptyCraneOut',
];

interface StoragePricingBase {
  loaded: number;
  empty: number;
  loadedCraneIn: number;
  loadedCraneOut: number;
  emptyCraneIn: number;
  emptyCraneOut: number;
}

export type StoragePricing = Record<ContainerType, StoragePricingBase> & {
  freeDays: number;
  effectiveDate: Moment | string;
  currencyCode: CurrencyCode;
};

export interface Pricing {
  loaded: number;
  empty: number;
  loadedCraneIn: number;
  loadedCraneOut: number;
  emptyCraneIn: number;
  emptyCraneOut: number;
}

export interface StoragePricingContainers {
  '20DC': Pricing;
  '20HC': Pricing;
  '20RF': Pricing;
  '40DC': Pricing;
  '40HC': Pricing;
  '40RF': Pricing;
  '40OT': Pricing;
  '40FR': Pricing;
  '45HC': Pricing;
}

const PRICING = {
  loaded: 0,
  empty: 0,
  loadedCraneIn: 0,
  loadedCraneOut: 0,
  emptyCraneIn: 0,
  emptyCraneOut: 0,
};

export const STORAGE_PRICING = {
  freeDays: 0,
  '20DC': PRICING,
  '20HC': PRICING,
  '20RF': PRICING,
  '40DC': PRICING,
  '40HC': PRICING,
  '40RF': PRICING,
  '40OT': PRICING,
  '40FR': PRICING,
  '45HC': PRICING,
};

export const SALES_FEE = {
  '20DC': 0,
  '20HC': 0,
  '20RF': 0,
  '40DC': 0,
  '40HC': 0,
  '40RF': 0,
  '40OT': 0,
  '40FR': 0,
  '45HC': 0,
};

export type SalesFeeContainers = typeof SALES_FEE;

export type SalesFee = typeof SALES_FEE & {
  effectiveDate: Moment | string;
};

export const CURRENCIES: Record<CurrencyCode, { code: CurrencyCode; symbol: string }> = {
  USD: {
    code: 'USD',
    symbol: '$',
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
  },
  RUB: {
    code: 'RUB',
    symbol: '₽',
  },
  UZS: {
    code: 'UZS',
    symbol: 'SUM',
  },
  TRY: {
    code: 'TRY',
    symbol: '₺',
  },
  KZT: {
    code: 'KZT',
    symbol: '₸',
  },
};

export const CONTAINER_MOCK_DATA: ContainerGetAllQuery['data'][number] = {
  number: 'TGHU1234567',
  id: 'mock-container-id',
  clientYardId: 'mock-client-yard-id',
  type: '20DC' as any,
  condition: 'CW' as any,
  canTrade: true,
  isHold: false,
  block: 'A',
  row: '1',
  slot: '1',
  tier: '1',
  status: 'GATED_IN',
  comment: 'Mock container',
  images: [
    {
      url: 'https://storage.googleapis.com/tezport-dev-container-images/1778735928884_OOLU7828801.jpg?GoogleAccessId=tezport-dev%40tezport-401601.iam.gserviceaccount.com&Expires=1893438000&Signature=Jp4606ihnvbAE%2FbYNH42gzyAf6RPGYfjp0qk2HGnA6L1p7m9ddjVSXQkPN33%2FFWl8lXBhyIfu%2BEtSUkMZXCT9UMyHPsQ2HeXfUe0bEN7KAo2EGniEeuclxAl8elvI8JksgAZhLBzW75o6Jehhw30VNPiIlKhuZp9d6nfW4n8LIZSUGbCQTIgM1soRKAIG4XxmP1JStqc9RI3VDYDJaQHcuz6RakaeysOaE%2BPiX0C2lIQwUkYpsBo621HAOhFnzJkx%2Fjn1s3idMq7zJjJvel%2FgmUp%2FYPQPqWgR1JRWTHPLad7mV2qdGHRyXBpCZV5Qo0NTgv%2F8rhCaklsSYmzSKGntw%3D%3D',
    },
  ],
  documents: [],
  clientYard: {
    client: {
      code: 'TSRL1234',
      id: 'mock-client-id',
    },
    yard: {
      id: 'mock-yard-id',
      address: {
        streetAddress: 'Xonabot',
        city: 'Tashkent',
        zipCode: '100015',
        country: 'Uzbekistan',
      },
      name: 'Mock Depot',
    },
  },
  gateIn: {
    driver: {
      licensePlateNumber: '01A123BC',
    },
    completedAt: new Date(),
  },
  gateOut: null as any,
  sale: { status: 'FOR_SALE' },
  _count: {
    repair: 0,
  },
};

export const REPAIRS_GROUP_MOCK_DATA: RepairGetAllGroupQuery['data'][number] = {
  clientYard: {
    client: {
      code: 'TSRL1234',
    },
  },
  id: 'mock-repair-group-id',
  number: 'TGHU1234567',
  createdAt: new Date().toISOString(),
  images: [{ url: 'https://example.com/repair-image.jpg' }],
  repair: [
    {
      price: 150,
      currencyCode: 'USD',
      status: 'ACTIVE',
    },
    {
      price: 50,
      currencyCode: 'USD',
      status: 'PENDING',
    },
    {
      price: 80,
      currencyCode: 'USD',
      status: 'COMPLETED',
    },
  ],
  customRepair: {
    price: '$280',
    pendingCount: 1,
    activeCount: 1,
    uncreatedInvoiceCount: 1,
  },
};

export const INVOICES_MOCK_DATA = {
  id: 'cmmj8jwxe000cou1092t61v5h',
  legalName: 'Test Client Refferr',
  code: 'TCRJ',
  currencyCode: 'USD',
  storagePaidTo: '2026-03-08T18:59:59.999Z',
  storageAmount: 0,
  cranePaidTo: '2026-03-08T18:59:59.999Z',
  craneAmount: 0,
  otherPaidTo: null,
  otherAmount: 0,
};

export const DEPOT_OCCUPANCY_TREND_MOCK_DATA = {
  historyStartPercent: 35,
  currentPercent: 50,
  forecastEndPercent: 150,
} as const;

export const YARD_PLACE_SCENE_MOCK_DATA: YardPlaceSceneData = {
  blocks: [
    {
      name: 'A',
      rowsLength: 6,
      slotsLength: 4,
      rotate: 0,
      position: {
        x: 0,
        y: 0,
      },
      roads: ['right'],
    },
    {
      name: 'B',
      rowsLength: 3,
      slotsLength: 3,
      rotate: 0,
    },
    {
      name: 'C',
      rowsLength: 3,
      slotsLength: 3,
      rotate: 0,
    },
  ],
  items: [
    {
      type: 'railroad-tracks',
    },
  ],
  containers: [
    {
      id: 'mock-container-id1',
      number: 'TGHU1234100',
      tier: 1,
      key: 'A-1-1',
      length: 20, // 20DC,20HC
    },
    {
      id: 'mock-container-id2',
      number: 'TGHU1234200',
      tier: 1,
      key: 'A-2-1,A-3-1',
      length: 40, // 40DC,40HC,45HC
    },
    {
      id: 'mock-container-id3',
      number: 'TGHU1234300',
      tier: 1,
      key: 'A-4-1',
      length: 20,
    },
    {
      id: 'mock-container-id4',
      number: 'TGHU1234400',
      tier: 1,
      key: 'A-5-1',
      length: 20,
    },
    {
      id: 'mock-container-id5',
      number: 'TGHU1234500',
      tier: 3,
      key: 'A-4-1,A-5-1',
      length: 40,
    },
  ],
};
